import { inject, observer } from "mobx-react";
import React from "react";
import { useDrag } from "react-dnd";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { runInAction } from "mobx";
import { formatDistanceToNow } from "date-fns";
import { sv, enGB } from "date-fns/locale";

import {
  Avatar,
  Button,
  Modal,
  MultiSelect,
  Input,
  Toggle,
  Icon,
  Text,
  Spacer
} from "src/components";
import { ConfirmationDialog } from "src/components/confirmDialog";
import { theme } from "src/config";
import { User } from "src/models";
import { AuthStore, RoutingStore, UserStore, SpaceStore } from "src/stores";
import { DataType, UserType, UserStatus, UserRole } from "src/types";
import styled from "styled-components";
import { Card } from "./card";
import { Column, Row } from "src/pages/room/editData/gridElements";
import toast from "react-hot-toast";
import { useClickOutside } from "src/util/util";

export interface IUserCardProps {
  authStore?: AuthStore;
  data: User;
  spaceStore?: SpaceStore;
  routingStore?: RoutingStore;
  space_id?: number;
  userStore?: UserStore;
  onClick?(): void;
  onUnlink?(): void;
}

export const UserCard = inject("authStore", "userStore", "routingStore", 'spaceStore')(observer((props: IUserCardProps): JSX.Element => {
  const { data, authStore, userStore, space_id, spaceStore } = props;
  const { getCurUser: currentUser } = authStore;
  const { t, i18n } = useTranslation();

  const cardRef = React.useRef<HTMLDivElement>(null);

  const [showConfirmDialog, setShowConfirmDialog] = React.useState<'delete' | 'unlink'>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DataType.User,
    item: { item: data, type: DataType.User, key: space_id },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }));

  const userRoles = [
    { label: t("owner") , value: UserRole.Owner  },
    { label: t("leader"), value: UserRole.Leader },
    { label: t("member"), value: UserRole.Member },
    { label: t("viewer"), value: UserRole.Viewer },
  ].filter(u => {
    if (u.value == UserRole.Owner && !spaceStore.inWorkspace) return false;
    return true;
  });

  const getRank = (role: UserRole) => {
    return [UserRole.Owner, UserRole.Leader, UserRole.Member, UserRole.Viewer].indexOf(role);
  }

  const filteredUserRoles = userRoles.filter(u => {
    if (currentUser.getRole(space_id) == UserRole.Owner) return true;
    if ([UserRole.Leader, UserRole.Owner].includes(currentUser.getRole(space_id)) && [UserRole.Leader, UserRole.Member, UserRole.Viewer].includes(u.value)) return true;
    return false;
  });

  const displayOptions = [
    { label: t("name"), value: "name" },
    { label: t("title"), value: "title" },
    { label: t("initials"), value: "initials" }
  ];

  const locale = i18n.language == 'sv' ? sv : enGB;

  useClickOutside(cardRef, () => {
    if (userStore.getActive && data.id === userStore.getActive.id && !showConfirmDialog) {
      userStore.setActive(null, true);
    }
  });

  const canDelete = (spaceStore.inWorkspace && getRank(currentUser.getRole(space_id)) <= getRank(data.getRole(space_id)));
  const canUnlink = getRank(currentUser.getRole(space_id)) <= getRank(data.getRole(space_id));
  const unlinkIcon = !canDelete ? 'delete' : 'unlink';

  return (
    <Card
      ref={(el) => { cardRef.current = el; drag(el); }}
      grayOut={userStore.getActive?.getGroupUsers.includes(data.id)}
      body={
        <UserContainerHead>
          {data.getStatus(space_id) === UserStatus.Invited && (
            <InvitedBadge>{t(UserStatus.Invited)}</InvitedBadge>
          )}
          <Avatar size="medium" image={data.getAvatar} initials={data.getInitials} />
          <Box width="12rem">
            <Text size="small">{data.getFullName}</Text>
            {data.type === UserType.Default && (
              <Text size="xsmall" color="grayDefault" truncated>{data.email}</Text>
            )}
          </Box>
        </UserContainerHead>
      }
      expandedBody={
        <>
          {data.getStatus(space_id) === UserStatus.Invited && (
            <InvitedBadge>{t(UserStatus.Invited)}</InvitedBadge>
          )}
          <CardContainer>
            <UserContainerHead>
              <Avatar size="xlarge" image={data.getAvatar} initials={data.getInitials} />
              <Text weight="bold" size="small">{data.getFullName}</Text>
            </UserContainerHead>
            <UserCardContent>
              <Column>
                <StyledRow>
                  <Text weight="bold" size="small">{t("account_type")}:</Text>
                  <Spacer size="xxsmall" />
                  <Text size="small">{t(data.type)}</Text>
                </StyledRow>
                <StyledRow>
                  <Text weight="bold" size="small">{t("name")}:</Text>
                  <Spacer size="xxsmall" />
                  <Text truncated size="small">{data.displayName}</Text>
                </StyledRow>
                {data.type === UserType.Default && (
                  <StyledRow>
                    <Text weight="bold" size="small">{t("email")}:</Text>
                    <Spacer size="xxsmall" />
                    <Text truncated size="small">{data.email}</Text>
                  </StyledRow>
                )}

                <StyledRow>
                  <Text weight="bold" size="small">{t("company")}:</Text>
                  <Spacer size="xxsmall" />
                  <Text size="small">{data.company_name}</Text>
                </StyledRow>
                <StyledRow>
                  <Text weight="bold" size="small">{t("title")}:</Text>
                  <Spacer size="xxsmall" />
                  <Text size="small">{data.getTitle}</Text>
                </StyledRow>
                <StyledRow>
                  <Text weight="bold" size="small">{t("initials")}:</Text>
                  <Spacer size="xxsmall" />
                  <Text size="small">{data.getInitials}</Text>
                </StyledRow>
                {data.created_by && (
                  <StyledRow>
                    <Text weight="bold" size="small">{t("invited_by")}:</Text>
                    <Spacer size="xxsmall" />
                    <Text size="small">
                      {data.created_by
                        ? props.userStore.getItemByID(data.created_by)
                          .displayName
                        : null}
                    </Text>
                  </StyledRow>
                )}
                <StyledRow>
                  <Text weight="bold" size="small">
                    {data.getStatus(space_id) === UserStatus.Invited
                      ? t(UserStatus.Invited)
                      : t("member_since")}
                    :
                  </Text>
                  <Spacer size="xxsmall" />
                  <Text size="small">
                    {formatDistanceToNow(new Date(data.created_at), {
                      addSuffix: true
                      , locale
                    })}
                  </Text>
                </StyledRow>
                {/* <StyledRow>
                  <Text weight="bold" size="small">{t("last_login")}:</Text>
                  <Spacer size="xxsmall" />
                  <Text size="small">
                    {data.lastLogin
                      ? formatDistanceToNow(new Date(data.lastLogin), {
                        addSuffix: true,
                        locale
                      })
                      : t("never")}
                  </Text>
                </StyledRow> */}
              </Column>
            </UserCardContent>
            {currentUser.canEdit && (
              <UserSettingsContent>
                {data.type == UserType.Accountless && (
                  <>
                    <Input
                      type     = "text"
                      value    = {data.company_name}
                      label    = {t("company_name")}
                      disabled = {!authStore.getCurUser?.canEdit}
                      onChange = {(value) => data.set("company_name", value, true)}
                    />
                    <Input
                      type     = "text"
                      value    = {data.first_name}
                      label    = {t("first_name")}
                      disabled = {!authStore.getCurUser?.canEdit}
                      onChange = {(value) => data.set("first_name", value, true)}
                    />
                    <Input
                      type     = "text"
                      value    = {data.last_name}
                      label    = {t("last_name")}
                      disabled = {!authStore.getCurUser?.canEdit}
                      onChange = {(value) => data.set("last_name", value, true)}
                    />
                    <Input
                      type     = "text"
                      value    = {data.phone}
                      label    = {t("phone")}
                      disabled = {!authStore.getCurUser?.canEdit}
                      onChange = {(value) => data.set("phone", value, true)}
                    />
                  </>
                )}
                <Input
                  type     = "text"
                  value    = {data.getTitleBySpace(space_id) || ""}
                  label    = {t("title")}
                  disabled = {!(authStore.getCurUser?.canEdit || data.id === authStore.getCurUserID)}
                  onChange = {(value) => data.setSpaceSetting(space_id, "title", value, true)}
                />
                <Input
                  type      = "text"
                  value     = {data.getInitialsBySpace(space_id) || ""}
                  label     = {t("initials")}
                  disabled  = {!(authStore.getCurUser?.canEdit || data.id === authStore.getCurUserID)}
                  maxLength = {Number("4")}
                  onChange  = {(value) => data.setSpaceSetting(space_id, "initials", value, true)}
                />
                {( data.id != currentUser.id ) && (
                  <MultiSelect
                    forceSelect
                    disableSearch
                    singleSelect
                    key      = "role"
                    label    = {t("role")}
                    options  = {filteredUserRoles}
                    selected = {[
                      {
                        value: data.getRole(space_id),
                        label: t(data.getRole(space_id))
                      }
                    ]}
                    onChange={async (option) => {
                      data.setRole(space_id, option.value);
                      await data.setSpaceSetting(space_id, "role", option.value, true);
                    }}
                  />
                )}
                <MultiSelect
                  forceSelect
                  disableSearch
                  singleSelect
                  key      = "display_as"
                  label    = {t("display_as")}
                  options  = {displayOptions}
                  selected = {[
                    {
                      value: data.getSpaceSetting(space_id, "display_as") || "name",
                      label: t(data.getSpaceSetting(space_id, "display_as") || "name")
                    }
                  ]}
                  onChange={(option) => {
                    data.setSpaceSetting(space_id, "display_as", option.value, true);
                  }}
                />
                <Toggle
                  label   = {t("hide_in_matrix")}
                  value   = {data.getCurSpaceSettings?.hide_in_matrix || ""}
                  checked = {data.getCurSpaceSettings?.hide_in_matrix}
                  onClick = {() => 
                    data.setCurSpaceSetting("hide_in_matrix", !data.getCurSpaceSettings?.hide_in_matrix, true)
                  }
                />
                <Toggle
                  label   = {t("hide_in_list")}
                  value   = {data.getCurSpaceSettings?.hide_in_list || ""}
                  checked = {data.getCurSpaceSettings?.hide_in_list}
                  onClick = {() => 
                    data.setCurSpaceSetting("hide_in_list", !data.getCurSpaceSettings?.hide_in_list, true)
                  }
                />
              </UserSettingsContent>
            )}
            <ButtonContainer>
              <Modal show={showConfirmDialog === 'delete'}>
                <ConfirmationDialog
                  onCancel  = {() => setShowConfirmDialog(null)}
                  label     = {t("confirm_action")}
                  message   = {t("confirm_action_delete_user_message")}
                  onConfirm = {() => {
                    const removed = data.remove();
                    if (removed) {
                      userStore.setActive(null);
                      toast.success(t("remove_user"), {
                        icon: <Icon size="medium" iconId="delete" />
                      });
                      setShowConfirmDialog(null)
                    } else {
                      toast.error(t("unable_to_remove_user"), {
                        icon: <Icon size="medium" iconId="delete" />
                      });
                    }

                  }}
                />
              </Modal>
              <Box display="flex" flexDirection="row-reverse">
                {(currentUser.canEdit && currentUser.id != data.id && canDelete) && (
                  <Button
                    variant="icon"
                    onClick={() => setShowConfirmDialog('delete')}
                  >
                    <Icon size="large" iconId="delete" color={theme.color.grayDark} />
                  </Button>
                )}

                <Modal show={showConfirmDialog === 'unlink'}>
                  <ConfirmationDialog
                    onCancel  = {() => setShowConfirmDialog(null)}
                    label     = {t("confirm_action")}
                    message   = {t("confirm_action_unlink_user_from_space_message")}
                    onConfirm = {async () => {
                      userStore.setActive(null);
                      try {
                        await data.unlinkSpace(space_id);
                        toast.success(t("unlinked_user"));
                        setShowConfirmDialog(null)
                      } catch (err) {
                        console.error("Failed to unlink user", err);
                        toast.error(t("unlink_user_failed"));
                      }
                    }}
                  />
                </Modal>

                {(currentUser.canEdit && currentUser.id != data.id && canUnlink) && (
                  <Button
                    variant = "icon"
                    onClick = {() => setShowConfirmDialog('unlink')}
                  >
                    <Icon size="large" iconId={unlinkIcon} color={theme.color.grayDark} />
                  </Button>
                )}
              </Box>
              {currentUser.canEdit && data.getStatus(space_id) === UserStatus.Invited && (
                <Button
                  size    = "small"
                  variant = "primary"
                  onClick = {() => authStore.resendInvite(data)}
                >
                  <Icon iconId="send" />
                  {t("resend_invite")}
                </Button>
              )}
            </ButtonContainer>
          </CardContainer>
        </>
      }
      key      = {data.id}
      editable = {currentUser.canEdit}
      height   = "auto"
      width    = "34rem"
      expanded = {Boolean(data.id === userStore.getActive?.id)}
      onClick  = {props.onClick || ((e) => userStore.setActive(data))}
      footer   = {
        <Footer>
          <Text size="small" truncated>
            {t(data.getRole(space_id))}
          </Text>
          <Icon iconId="person" size="medium" color={theme.color.grayDark} />
        </Footer>
      }
    />
  );
})
);


const Footer = styled.div`
  display:         flex;
  justify-content: space-between;
  flex-direction:  row;
  width:           100%;
  align-items:     center;
`;

const UserContainerHead = styled.div`
  flex-direction: row;
  align-items:    center;
  display:        flex;
  gap:            1rem;
`;

const CardContainer = styled.div`
  display:        flex;
  flex-direction: column;
  gap:            1rem;
`;

const UserSettingsContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
`;

const UserCardContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
  margin:         1rem 0rem;
`;

const ButtonContainer = styled.div`
  display:         flex;
  width:           100%;
  justify-content: space-between;
  flex-direction:  row-reverse;
`;

const InvitedBadge = styled.span`
  padding:         0.4rem 0.6rem;
  border-radius:   0.3rem;
  background:      ${theme.color.primary8};
  position:        absolute;
  top:             -1rem;
  right:           1rem;
  color:           ${theme.color.white};
  font-size:       0.5rem;
  display:         flex;
  font-size:       ${theme.font.size.xsmall};
  align-items:     center;
  text-align:      center;
  justify-content: center;
`;

const StyledRow = styled(Row)`
  align-items: baseline;
`;
